import React, { useCallback, useEffect, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";

import { Button, CustomSelect, Error, Label, Loading } from "../..";
import { getAxios, postAxios, putAxios } from "../../../Services/commonService";
import {
  API_USER,
  API_USER_EVENT_TYPE,
  API_USER_OPTIONS,
  API_USER_ROLE_OPTIONS,
  API_WHITE_LABEL_OPTIONS,
} from "../../../../utils/api/ApiConstant";
import { AddUserSchema } from "../../../Schema/RoleManagement/UserSchema";
import Icon from "../../../../assets/icons/Icon";
import { lowerFirstLetterUsingRegex } from "../../../helper/common";
import {
  selectLoggedInUserId,
  selectLoggedInUserType,
} from "../../../../store/user/user-selector";
import {
  staticRoleType,
  staticUserType,
} from "../../../../store/globalSettings";
import SvgIcon from "../../UI/Svg";

const AddEditUserModal = ({ rowData, handleModalHide, fetchData }) => {
  // selector
  const LoggedInUserId = useSelector(selectLoggedInUserId);
  const LoggedInUserType = useSelector(selectLoggedInUserType);

  const isEdit = !!rowData?.id;
  const isCreatedByParentMode = !!rowData?.selectedRowId;

  const [showHide, setShowHide] = useState(false);
  const [selectAllEvents, setSelectAllEvents] = useState(false);

  // refs
  const editModeLoaded = useRef(false);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
    setError,
    setValue,
    watch,
  } = useForm({
    // resolver: yupResolver(AddUserSchema),
    defaultValues: {
      name: "",
      userName: "",
      password: "",
      roleId: null,
      parentId: null,
      sharing: [],
      wlUniqueId: "",
    },
  });

  const SharingWatch = watch("sharing");
  console.log("🚀 ~ AddEditUserModal ~ SharingWatch:", SharingWatch);
  const ParentIdWatch = watch("parentId");
  const UsernameWatch = watch("userName");
  const RoleWatch = watch("roleId");

  // roles options
  const { data: roles } = useQuery({
    queryKey: ["roles", ParentIdWatch?.value],
    queryFn: () =>
      getAxios(`${API_USER_ROLE_OPTIONS}/${ParentIdWatch?.value}`, {
        self: true,
      }),
    enabled: !!ParentIdWatch?.value,
  });

  // user data
  const { data: userData } = useQuery({
    queryKey: ["user", rowData?.id],
    queryFn: async () => await getAxios(`${API_USER}/${rowData?.id}`),
    enabled: isEdit,
    staleTime: 0,
    gcTime: 0,
  });

  // event options
  const { data: eventTypeData, isLoading } = useQuery({
    queryKey: ["eventType", ParentIdWatch?.value],
    queryFn: () => getAxios(`${API_USER_EVENT_TYPE}/${ParentIdWatch?.value}`),
    enabled: !!ParentIdWatch?.value,
    staleTime: 0,
    gcTime: 0,
  });

  // white label options
  const { data: whitelabelData } = useQuery({
    queryKey: ["whitelabel", LoggedInUserId],
    queryFn: () => getAxios(API_WHITE_LABEL_OPTIONS),
    enabled: !!LoggedInUserId && LoggedInUserType === staticUserType.FA,
  });

  // parent user options
  const { data: parentUserListData } = useQuery({
    queryKey: ["parentUserList", LoggedInUserId],
    queryFn: () => getAxios(API_USER_OPTIONS),
    enabled: !!LoggedInUserId,
  });

  const handleSelectionParentOption = useCallback(
    (parnetId = null) => {
      if (!parnetId) return;

      const matchedParent = parentUserListData?.find(
        (parent) => parent.value === parnetId,
      );

      if (!matchedParent) return;

      setValue("parentId", matchedParent || null);
    },
    [setValue, parentUserListData],
  );

  // using for set parent id through created by parent mode
  useEffect(() => {
    if (!isCreatedByParentMode || !parentUserListData) return;

    handleSelectionParentOption(rowData?.selectedRowId);
  }, [isCreatedByParentMode, parentUserListData]);

  // using for set event type in default data
  useEffect(() => {
    if (!eventTypeData) return;

    let modify;

    if (!isEdit || editModeLoaded.current) {
      modify = eventTypeData.map((x) => ({
        ...x,
        plDown: 0,
        plUp: x?.plDown ?? 0,
        plOur: x?.plDown ?? 0,
        checked: false,
      }));
    } else {
      if (!userData?.lstUserEventTypeSharing || editModeLoaded.current) return;

      let isChecked = true;

      modify = eventTypeData.map((x) => {
        const match = userData?.lstUserEventTypeSharing?.find((event) => {
          return event.eventTypeId === x?.value;
        });

        let updated;

        if (isChecked) {
          isChecked = match.eventTypeAssigned;
        }

        if (match) {
          updated = {
            ...match,
            checked: match?.eventTypeAssigned,
            label: match?.eventTypeName,
            value: match?.eventTypeId,
            plOur: !match?.eventTypeAssigned ? match?.plUp : match?.plOur,
          };
        }

        return updated;
      });

      editModeLoaded.current = true;
      setSelectAllEvents(isChecked);
    }

    setValue("sharing", modify);
  }, [eventTypeData, isEdit, userData?.lstUserEventTypeSharing]);

  // using for set role value on edit mode
  // bcz role is dependant for parant id , so need to saparte effect for this role seletion
  useEffect(() => {
    if (!isEdit || !userData || !roles) return;

    const matchedRole = roles.find((role) => role.value === userData?.roleId);

    if (matchedRole) setValue("roleId", matchedRole);
  }, [isEdit, userData, roles]);

  // using for set white label value on edit mode with all conditions
  useEffect(() => {
    if (
      !isEdit ||
      !userData ||
      !whitelabelData ||
      LoggedInUserType !== staticUserType?.FA ||
      RoleWatch?.label !== staticRoleType?.superadmin
    )
      return;

    const matchedWhiteLabel = whitelabelData.find(
      (wl) => wl.value === userData?.wlUniqueId,
    );

    if (matchedWhiteLabel) setValue("wlUniqueId", matchedWhiteLabel);
  }, [
    isEdit,
    userData,
    whitelabelData,
    staticUserType,
    RoleWatch?.label,
    staticRoleType,
  ]);

  // Changed: added isSuccess as condition (like file 1)
  useEffect(() => {
    if (!isEdit || !userData || !parentUserListData) return;

    const matchedParent = parentUserListData?.find(
      (parent) => parent.value === userData?.parentId,
    );

    setValue("name", userData?.name || "");
    setValue("userName", userData?.userName || "");
    setValue("password", userData?.password || "");
    setValue("parentId", matchedParent || null);
  }, [isEdit, userData, parentUserListData]);

  // Changed: fixed submit - was passing empty {} as body, now sends proper payload
  const onSubmit = async (values) => {
    const { sharing, ...payload } = values;

    const isCL = payload?.roleId?.userType === staticUserType?.CL;

    const modifySharing = values?.sharing
      .filter((share) => share.checked)
      .map((x) => ({
        ...x,
        eventTypeId: x?.value,
        // eventTypeAssigned: x?.checked,
        ...(isCL && {
          plUp: 0,
          plDown: 0,
          plOur: 0,
        }),
      }));

    payload.parentId = values?.parentId?.value;
    payload.roleId = values?.roleId?.value;
    payload.lstUserEventTypeSharing = modifySharing;

    const url = `${API_USER}${isEdit ? "/" + rowData?.id : ""}`;
    const method = isEdit ? putAxios : postAxios;

    if (LoggedInUserType === staticUserType.FA) {
      payload.wlUniqueId = values?.wlUniqueId?.value;
    }

    if (isEdit) {
      payload.isActive = rowData?.isActive;
      payload.rowVersion = rowData?.rowVersion;
    }

    const res = await method(url, payload);
    if (res?.success) {
      fetchData();
      handleModalHide();
      return;
    }

    res?.errors?.forEach((element) => {
      const field = lowerFirstLetterUsingRegex(element?.field);
      if (field)
        setError(field, {
          type: "manual",
          message: element.message,
        });
    });
  };

  const handleSelectAll = (e) => {
    const checked = e.target.checked;

    setSelectAllEvents(checked);

    const updated = (SharingWatch || []).map((x) => ({
      ...x,
      checked,
    }));

    setValue("sharing", updated);
  };

  // using unmount reset form fields
  useEffect(() => {
    return () => {
      editModeLoaded.current = false;
      // debugger;
      reset({
        name: "",
        userName: "",
        password: "",
        roleId: null,
        parentId: null,
        sharing: [],
        wlUniqueId: "",
      });
    };
  }, []);

  return (
    <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
      <fieldset disabled={isSubmitting}>
        <div className="row m-0 p-0">
          {/* parent field */}
          <div className="col-md-3">
            <div className="form-group">
              <Label
                htmlFor="parentId"
                className="form-label"
                isRequired={true}
              >
                Parent
              </Label>
              <Controller
                name="parentId"
                control={control}
                render={({ field }) => (
                  <CustomSelect
                    {...field}
                    options={parentUserListData}
                    value={field.value}
                    onChange={(selectedOption) => {
                      // debugger;
                      field.onChange(selectedOption);
                      // reset dependent fields
                      setValue("roleId", null);
                      setValue("wlUniqueId", null);
                    }}
                    placeholder="Select Parent"
                    isMulti={false}
                    isDisabled={isSubmitting}
                    autoFocus={true}
                  />
                )}
              />
              {errors.parentId && <Error>{errors.parentId.message}</Error>}
            </div>
          </div>

          {/* Role Field */}
          <div className="col-md-3">
            <div className="form-group">
              <Label htmlFor="roleId" className="form-label" isRequired={true}>
                Role
              </Label>
              <Controller
                name="roleId"
                control={control}
                render={({ field }) => (
                  <CustomSelect
                    {...field}
                    options={roles}
                    value={field.value}
                    onChange={(selectedOption) => {
                      field.onChange(selectedOption);
                      setValue("wlUniqueId", null);
                    }}
                    placeholder="Select Role"
                    isMulti={false}
                    isDisabled={isSubmitting}
                  />
                )}
              />
              {errors.roleId && <Error>{errors.roleId.message}</Error>}
            </div>
          </div>

          {/* white label */}
          {LoggedInUserType === staticUserType.FA &&
            RoleWatch?.label === staticRoleType.superadmin && (
              <div className="col-md-3">
                <div className="form-group">
                  <Label
                    htmlFor="wlUniqueId"
                    className="form-label"
                    isRequired={true}
                  >
                    White Label
                  </Label>
                  <Controller
                    name="wlUniqueId"
                    control={control}
                    render={({ field }) => (
                      <CustomSelect
                        {...field}
                        options={whitelabelData}
                        value={field.value}
                        onChange={(selectedOption) =>
                          field.onChange(selectedOption)
                        }
                        placeholder="Select White Label"
                        isMulti={false}
                        isDisabled={isSubmitting}
                      />
                    )}
                  />
                  {errors.wlUniqueId && (
                    <Error>{errors.wlUniqueId.message}</Error>
                  )}
                </div>
              </div>
            )}
        </div>

        <div className="row m-0 p-0">
          {/* Username Field */}
          <div className="col-md-3">
            <div className="form-group">
              <Label
                htmlFor="userName"
                className="form-label"
                isRequired={true}
              >
                Username
              </Label>
              <input
                type="text"
                className="form-control"
                id="userName"
                placeholder="Enter username"
                {...register("userName", {
                  onChange: (e) => {
                    e.target.value = e.target.value.replace(/\s/g, "");
                  },
                })}
              />
              {errors.userName && <Error>{errors.userName.message}</Error>}
            </div>
          </div>

          {/* password */}
          <div className="col-md-3">
            <div className="form-group">
              <Label
                htmlFor="password"
                className="form-label"
                isRequired={true}
              >
                Password
              </Label>
              <div className="password-field">
                <input
                  type={showHide ? "text" : "password"}
                  className="form-control"
                  id="password"
                  placeholder="Enter password"
                  {...register("password")}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowHide((prev) => !prev)}
                  aria-label={showHide ? "Hide password" : "Show password"}
                >
                  {showHide ? <Icon name="EyeClosed" /> : <Icon name="Eye" />}
                </button>
              </div>
              {errors.password && <Error>{errors.password.message}</Error>}
            </div>
          </div>

          {/* Name Field */}
          <div className="col-md-3">
            <div className="form-group">
              <Label htmlFor="name" className="form-label" isRequired={true}>
                Name
              </Label>
              <input
                type="text"
                className="form-control"
                id="name"
                placeholder="Enter name"
                {...register("name", {
                  onChange: (e) => {
                    e.target.value = sanitizeNameInput(e.target.value);
                  },
                })}
              />
              {errors.name && <Error>{errors.name.message}</Error>}
            </div>
          </div>

          {/* sharing */}
          {SharingWatch && (
            <div className="permission-card">
              <div className="heading_model">
                <h5 className="section-title">
                  Profit/Loss Sharing <span className="text-danger">*</span>
                </h5>
              </div>

              <div className="table-responsive">
                <table className="report-card-box ">
                  <thead>
                    <tr>
                      <th rowSpan={2}>
                        <div className="form-check justify-content-center">
                          <input
                            className="form-check-input "
                            type="checkbox"
                            onChange={handleSelectAll}
                            disabled={
                              !SharingWatch || SharingWatch?.length === 0
                            }
                            checked={selectAllEvents}
                          />
                        </div>
                      </th>
                      <th rowSpan={2}>Events</th>
                      {RoleWatch?.userType !== staticUserType?.CL && (
                        <>
                          <th colSpan={2}>
                            <b>{ParentIdWatch?.label}</b>&nbsp;(Parent)
                          </th>
                          <th>
                            <b>{UsernameWatch}</b> (User)
                          </th>
                        </>
                      )}
                    </tr>
                    {RoleWatch?.userType !== staticUserType?.CL && (
                      <>
                        <tr>
                          <th>Total</th>
                          <th>Remaining</th>
                          <th>Assign</th>
                        </tr>
                      </>
                    )}
                  </thead>

                  <tbody>
                    {isLoading && (
                      <tr>
                        <td colSpan={4}>
                          <Loading />
                        </td>
                      </tr>
                    )}

                    {!isLoading &&
                      (!SharingWatch || SharingWatch?.length === 0) && (
                        // <tr>
                        //   <td colSpan={5} align="center">
                        //     No events to show.
                        //   </td>
                        // </tr>
                        <tr className="not_rows_tr">
                          <td colSpan={5} className="text-center">
                            <div className="not_rows ">
                              <SvgIcon className="not_icon" name="notData" />
                              No events to show
                            </div>
                          </td>
                        </tr>
                      )}

                    {!isLoading &&
                      SharingWatch &&
                      SharingWatch?.length > 0 &&
                      SharingWatch?.map((event, index) => (
                        <tr key={index}>
                          <td align="center">
                            <div className="form-check justify-content-center">
                              <input
                                className="form-check-input "
                                type="checkbox"
                                {...register(`sharing.${index}.checked`)}
                                onChange={(e) => {
                                  const checked = e.target.checked;

                                  setValue(`sharing.${index}.checked`, checked);

                                  const updated = SharingWatch.map((item, i) =>
                                    i === index ? { ...item, checked } : item,
                                  );

                                  const allChecked = updated?.every(
                                    (x) => x.checked === true,
                                  );
                                  setSelectAllEvents(!!allChecked);

                                  setValue("sharing", updated);
                                }}
                              />
                            </div>
                          </td>
                          <td>{event?.label}</td>

                          {RoleWatch?.userType !== staticUserType?.CL && (
                            <>
                              <td>
                                <div className="input_box ">
                                  <input
                                    className="form-control width-100 text-end"
                                    type="number"
                                    {...register(`sharing.${index}.plUp`)}
                                    disabled={true}
                                  />
                                </div>
                              </td>

                              <td>
                                <div className="input_box ">
                                  <input
                                    className="form-control width-100 text-end"
                                    type="number"
                                    {...register(`sharing.${index}.plOur`)}
                                    disabled={true}
                                  />
                                </div>
                              </td>

                              <td>
                                <div className="input_box ">
                                  <input
                                    className="form-control width-100 text-end"
                                    type="number"
                                    {...register(`sharing.${index}.plDown`, {
                                      onChange: (e) => {
                                        let down = Number(e.target.value) || 0;

                                        const up =
                                          Number(
                                            watch(`sharing.${index}.plUp`),
                                          ) || 0;

                                        // Rule 1: clamp down <= up
                                        if (down > up) down = up;

                                        // set corrected down
                                        setValue(
                                          `sharing.${index}.plDown`,
                                          down,
                                        );

                                        // Rule 2: calculate our = up - down
                                        setValue(
                                          `sharing.${index}.plOur`,
                                          up - down,
                                        );
                                      },
                                    })}
                                    onFocus={(e) => e.target.select()}
                                  />
                                </div>
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="d-flex justify-content-end w-100 mt-3">
          <Button
            type="button"
            className="btn btn_main btn_muted px-3 py-2 mx-1"
            onClick={handleModalHide}
          >
            Close
          </Button>
          {/* Changed: button text now shows Loading../Update/Create instead of Loading.../Submit */}
          <Button
            type="submit"
            className="btn btn_main px-3 py-2 mx-1"
            isDisabled={isSubmitting}
          >
            {isSubmitting ? "Loading.." : isEdit ? "Update" : "Create"}
          </Button>
        </div>
      </fieldset>
    </form>
  );
};

export default AddEditUserModal;
